import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { auth, db, storage } from '../firebase';

const styles = {
  screen: {
    minHeight: '100vh',
    backgroundColor: '#F4F6F9',
    fontFamily: 'system-ui, sans-serif',
  },
  main: {
    marginLeft: 220,
    padding: 24,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#0A2D5A',
    marginBottom: 4,
  },
  pageSub: {
    fontSize: 13,
    color: '#888',
    marginBottom: 24,
  },
  form: {
    backgroundColor: 'white',
    borderRadius: 12,
    border: '0.5px solid #e0e0e0',
    padding: 24,
    maxWidth: 600,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0A2D5A',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 16,
    marginTop: 24,
    paddingBottom: 8,
    borderBottom: '1px solid #eee',
  },
  field: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: '600',
    color: '#555',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '0.5px solid #f0f0f0',
  },
  toggleLabel: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  toggleSub: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  submitBtn: {
    backgroundColor: '#E8620A',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 15,
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: 8,
  },
  success: {
    backgroundColor: '#EAF3DE',
    color: '#3B6D11',
    padding: '12px 16px',
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 16,
    fontWeight: '600',
  },
};

export default function Profile() {
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photoURL, setPhotoURL] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    agency: '',
    email: '',
    phone: '',
    tollFree: '',
    calendly: '',
    title: '',
    anocButtons: false,
    partDReviewEnabled: false,
  });

  const agentId = auth.currentUser?.uid;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!agentId) return;
      const ref = doc(db, 'agents', agentId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setForm(prev => ({
          ...prev,
          ...data,
          partDReviewEnabled: data.partDReviewEnabled ?? data.partDToggle ?? false,
        }));
        if (data.photoURL) setPhotoURL(data.photoURL);
      }
    };
    fetchProfile();
  }, [agentId]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleManageBilling = async () => {
    try {
      const fns = getFunctions();
      const call = httpsCallable(fns, 'createBillingPortalSession');
      const res = await call();
      if (res.data && res.data.url) { window.location.href = res.data.url; }
    } catch (err) {
      console.error('Billing portal error:', err);
      alert('Could not open billing. Please contact support.');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const photoRef = ref(storage, `agents/${agentId}/photo`);
      await uploadBytes(photoRef, file);
      const url = await getDownloadURL(photoRef);
      setPhotoURL(url);
      await setDoc(doc(db, 'agents', agentId), { ...form, photoURL: url }, { merge: true });
    } catch (err) {
      console.error(err);
    }
    setPhotoUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await setDoc(doc(db, 'agents', agentId), {
        ...form,
        photoURL,
        updatedAt: new Date(),
      }, { merge: true });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  return (
    <div style={styles.screen}>
      <Sidebar />
      <div style={styles.main}>
        <div style={styles.pageTitle}>My Profile</div>
        <div style={styles.pageSub}>This information appears in your clients' apps.</div>

        <div style={styles.form}>
          <form onSubmit={handleSubmit}>
            <div style={styles.sectionTitle}>Profile Photo</div>
            <div style={styles.field}>
              {photoURL && (
                <img src={photoURL} alt="Agent" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #E8620A', marginBottom: 12, display: 'block' }} />
              )}
              <label style={styles.label}>Upload Headshot</label>
              <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ fontSize: 13 }} />
              {photoUploading && <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Uploading...</div>}
              <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>This photo will appear in all your clients' apps.</div>
            </div>

            <div style={styles.sectionTitle}>Agent Information</div>
            <div style={styles.field}>
              <label style={styles.label}>Full Name</label>
              <input style={styles.input} value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                placeholder="Your full name" required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Title</label>
              <input style={styles.input} value={form.title}
                onChange={e => handleChange('title', e.target.value)}
                placeholder="e.g. Licensed Medicare Agent · 10 Years" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Agency Name</label>
              <input style={styles.input} value={form.agency}
                onChange={e => handleChange('agency', e.target.value)}
                placeholder="Your agency name" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Notification Email</label>
              <input style={styles.input} value={form.email}
                onChange={e => handleChange('email', e.target.value)}
                placeholder="your@email.com"
                type="email"
                required />
              <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>You will receive client alerts at this address.</div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Phone Number</label>
              <input style={styles.input} value={form.phone}
                onChange={e => handleChange('phone', e.target.value)}
                placeholder="Your phone number" required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Toll Free Number (Optional)</label>
              <input style={styles.input} value={form.tollFree}
                onChange={e => handleChange('tollFree', e.target.value)}
                placeholder="Your toll free number (optional)" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Scheduling Link (Calendly or similar)</label>
              <input style={styles.input} value={form.calendly}
                onChange={e => handleChange('calendly', e.target.value)}
                placeholder="https://calendly.com/your-link" />
            </div>

            <div style={styles.sectionTitle}>Notification Settings</div>
            <div style={styles.toggleRow}>
              <div>
                <div style={styles.toggleLabel}>September 1st ANOC Alert — Include Schedule & Call Buttons</div>
                <div style={styles.toggleSub}>Off = informational only. On = include Schedule and Call buttons.</div>
              </div>
              <div
                onClick={() => handleChange('anocButtons', !form.anocButtons)}
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 12,
                  backgroundColor: form.anocButtons ? '#E8620A' : '#ddd',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                }}>
                <div style={{
                  position: 'absolute',
                  top: 2,
                  left: form.anocButtons ? 22 : 2,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  transition: 'left 0.2s',
                }} />
              </div>
            </div>

            <div style={styles.toggleRow}>
              <div>
                <div style={styles.toggleLabel}>Part D Plan Review Service — I review Part D prescription drug plans with my clients during AEP</div>
                <div style={styles.toggleSub}>On = sends a September 5th Part D ANOC notice to Supplement clients and adds a Part D reminder to the September 28th alert.</div>
              </div>
              <div
                onClick={() => handleChange('partDReviewEnabled', !form.partDReviewEnabled)}
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 12,
                  backgroundColor: form.partDReviewEnabled ? '#E8620A' : '#ddd',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                  marginLeft: 16,
                }}>
                <div style={{
                  position: 'absolute',
                  top: 2,
                  left: form.partDReviewEnabled ? 22 : 2,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  transition: 'left 0.2s',
                }} />
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              {success && <div style={styles.success}>Profile saved successfully.</div>}
              <button style={styles.submitBtn} type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </form>

          <div style={{ backgroundColor: 'white', borderRadius: 12, border: '0.5px solid #e0e0e0', padding: 24, marginTop: 24, maxWidth: 640 }}>
            <div style={{ fontSize: 16, fontWeight: '700', color: '#0A2D5A', marginBottom: 16 }}>Subscription & Billing</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid #f0f0f0' }}>
              <span style={{ color: '#888', fontSize: 13 }}>Status</span>
              <span style={{ color: '#111', fontSize: 13, fontWeight: '600', textTransform: 'capitalize' }}>{form.accountStatus || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid #f0f0f0' }}>
              <span style={{ color: '#888', fontSize: 13 }}>Renews</span>
              <span style={{ color: '#111', fontSize: 13, fontWeight: '600' }}>{form.nextRenewalDate && form.nextRenewalDate.toDate ? form.nextRenewalDate.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#888', fontSize: 13 }}>Renewal amount</span>
              <span style={{ color: '#111', fontSize: 13, fontWeight: '600' }}>$49/year</span>
            </div>
            <button type="button" onClick={handleManageBilling} style={{ marginTop: 16, backgroundColor: '#1D6FA4', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: '600', cursor: 'pointer' }}>
              Manage Billing
            </button>
            <div style={{ fontSize: 12, color: '#888', marginTop: 10 }}>Update your card, view invoices, or cancel future renewals.</div>
          </div>
        </div>
      </div>
    </div>
  );
}